/**
 * Diagnoses — one row per analysis, showing the `diagnosis` object exactly as
 * the analyzer emitted it.
 *
 * The companion to By-pattern, and the opposite view. A pattern row is one key
 * aggregated across every student and attempt that produced it, which is why it
 * cannot be read back against a single model output: `lastKind` there is a
 * spread over carriers, `recurrence` is a sum, and `cause` — the sentence that
 * makes a diagnosis legible — is not in the aggregate at all. Here a row IS a
 * model output, and the columns are the five fields of the node.
 *
 * The column that exists nowhere else is `memory`: what `updateUserMemory` did
 * with this diagnosis. An invented `concept_id` is dropped to the category and
 * an unknown category is dropped entirely — both logged as warnings and, until
 * now, visible on no screen.
 *
 * Windowed by design: `generatedAt` is indexed and `assessmentDetails` is not,
 * so the date range is what keeps this affordable (7 days ≈ 123 RU against
 * prod's 21k model outputs; unbounded is ~1,000).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SearchFilterBar, DataTable, type FilterDef, type ColumnDef } from '@shared-ui/components';
import { fetchWithAdmin } from '../../api/fetchHelper';
import { formatWhen } from './format';
import type { DiagnosisRow, ListDiagnosesResponse } from './types';

const SUBJECTS = ['maths', 'physics', 'chemistry', 'biology'];
const CATEGORIES = [
  'calculation_slip', 'misread_question', 'recall_gap', 'understanding_gap',
  'application_error', 'analysis_error', 'evaluation_gap',
];

/** What the memory did with the row, in the memory's own vocabulary. */
const OUTCOME_TEXT: Record<DiagnosisRow['memoryOutcome'], string> = {
  concept: 'concept',
  category: 'category',
  'concept-rejected': 'concept rejected → category',
  dropped: 'dropped',
  none: '—',
};

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

export function DiagnosesPanel({ userId }: { userId?: string }) {
  const [applied, setApplied] = useState<Record<string, string>>({
    windowFrom: isoDay(new Date(Date.now() - 7 * 86_400_000)),
    windowTo: '',
    subject: '', kind: '', category: '', causeContains: '', rejectedOnly: '',
  });

  const filters: FilterDef[] = [
    { key: 'window', kind: 'daterange', label: 'Analysed', fromPlaceholder: 'from', toPlaceholder: 'to' },
    { key: 'subject', kind: 'select', placeholder: 'All subjects', options: SUBJECTS.map(s => ({ value: s, label: s })) },
    {
      key: 'kind', kind: 'select', placeholder: 'All kind',
      // Both vocabularies: v2 rows carry the first six, v1 rows the last two.
      options: ['slip', 'wrong_knowledge', 'missing_knowledge', 'undetermined', 'improvement', 'none', 'misapplied_rule', 'gap']
        .map(k => ({ value: k, label: k.replace(/_/g, ' ') })),
    },
    { key: 'category', kind: 'select', placeholder: 'All category', options: CATEGORIES.map(c => ({ value: c, label: c })) },
    { key: 'causeContains', kind: 'text', placeholder: 'cause contains' },
    {
      key: 'rejectedOnly', kind: 'select', placeholder: 'All outcomes',
      options: [{ value: 'true', label: 'rejected by the memory only' }],
    },
  ];

  const params = new URLSearchParams();
  if (applied.windowFrom) params.set('since', new Date(applied.windowFrom).toISOString());
  if (applied.windowTo) params.set('until', new Date(`${applied.windowTo}T23:59:59.999Z`).toISOString());
  for (const k of ['subject', 'kind', 'category', 'causeContains', 'rejectedOnly'] as const) {
    if (applied[k]) params.set(k, applied[k]);
  }
  if (userId) params.set('userId', userId);

  const q = useQuery({
    queryKey: ['diagnoses', applied, userId],
    queryFn: () => fetchWithAdmin<ListDiagnosesResponse>(`/console/diagnoses?${params}`),
  });

  const columns: ColumnDef<DiagnosisRow>[] = [
    {
      key: 'generatedAt', header: 'when', mobile: 'title', className: 'lp-col-when',
      render: r => formatWhen(r.generatedAt),
    },
    {
      key: 'userId', header: 'student', className: 'lp-col-student',
      render: r => (
        <div className="lp-namecol">
          <span className="lp-name">{r.userName ?? '—'}</span>
          <span className="lp-key">{r.userEmail ?? r.userId}</span>
        </div>
      ),
    },
    { key: 'subject', header: 'subject', className: 'lp-dim lp-col-trend', render: r => r.subject ?? '—' },
    {
      key: 'kind', header: 'kind', className: 'lp-col-trend',
      // A v2 finding is kind + stage; tentative is said, because it is not asserted anywhere else.
      render: r => r.kind
        ? `${r.kind.replace(/_/g, ' ')}${r.stage ? ` · ${r.stage.replace(/_/g, ' ')}` : ''}${r.certainty === 'tentative' ? ' · tentative' : ''}`
        : '—',
    },
    { key: 'cause', header: 'cause', render: r => <span className="lp-cause-cell">{r.cause || '—'}</span> },
    {
      key: 'concept', header: 'concept / concept_id',
      render: r => (
        <div className="lp-namecol">
          <span className="lp-name">{r.concept || '—'}</span>
          {r.conceptId && <span className="lp-key">{r.conceptId}</span>}
        </div>
      ),
    },
    { key: 'category', header: 'category', className: 'lp-dim', render: r => <span className="lp-key">{r.category || '—'}</span> },
    {
      key: 'memoryOutcome', header: 'memory', className: 'lp-col-when',
      render: r => (
        <span
          className={`lp-chip ${r.memoryOutcome === 'concept-rejected' || r.memoryOutcome === 'dropped' ? 'is-habit' : 'is-gap'}`}
          title={r.rejectedReason ?? ''}
        >
          {OUTCOME_TEXT[r.memoryOutcome]}
        </span>
      ),
    },
  ];

  return (
    <>
      <SearchFilterBar filters={filters} values={applied} onChange={setApplied} hideSearch />

      {q.isLoading && <p className="lp-muted">Loading…</p>}
      {q.isError && <p className="lp-error">Failed: {(q.error as Error).message}</p>}

      {q.data && (
        <p className="lp-count">
          {q.data.rows.length} diagnoses from {q.data.scanned} analyses in the window
          {q.data.truncated && <span className="lp-warn"> — capped, narrow the window for a complete picture</span>}
        </p>
      )}

      {q.data && (
        <DataTable
          data={q.data.rows}
          columns={columns}
          rowKey={r => r.modelOutputId}
          emptyMessage="No analysis in this window emitted a diagnosis."
        />
      )}
    </>
  );
}
